import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';

interface CustomDateDisplay {
  topContent?: React.ReactNode;
  middleContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
}

interface DateScrollerProps {
  selectedDate: Dayjs;
  onDateSelect: (date: Dayjs) => void;
  customDisplay?: (date: Dayjs) => CustomDateDisplay;
}

const DateScroller: React.FC<DateScrollerProps> = ({
  selectedDate,
  onDateSelect,
  customDisplay,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Generate 7 days for one week starting from the given date
  const generateWeekDates = useCallback((startDate: Dayjs): Dayjs[] => {
    const weekStart = startDate.startOf('week');
    const dateList: Dayjs[] = [];

    for (let i = 0; i < 7; i++) {
      dateList.push(weekStart.add(i, 'day'));
    }

    return dateList;
  }, []);

  const [currentWeekStart, setCurrentWeekStart] = useState<Dayjs>(() =>
    (selectedDate || dayjs()).startOf('week'),
  );

  const [dates, setDates] = useState<Dayjs[]>(() =>
    generateWeekDates(selectedDate || dayjs()),
  );

  useEffect(() => {
    if (selectedDate) {
      const newWeekStart = selectedDate.startOf('week');
      setCurrentWeekStart(newWeekStart);
      const newDates = generateWeekDates(selectedDate);
      setDates(newDates);
    }
  }, [selectedDate, generateWeekDates]);

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newWeekStart = currentWeekStart.add(
      direction === 'next' ? 1 : -1,
      'week',
    );
    setCurrentWeekStart(newWeekStart);
    const newDates = generateWeekDates(newWeekStart);
    setDates(newDates);
  };

  const getDateDisplay = (date: Dayjs): CustomDateDisplay => {
    if (customDisplay) {
      return customDisplay(date);
    }

    return {
      topContent: date.format('ddd'),
      middleContent: date.format('DD'),
      bottomContent: date.format('MMM'),
    };
  };

  return (
    <WeekContainer>
      <NavigationButton
        onClick={() => navigateWeek('prev')}
        icon={<LeftOutlined />}
        size="small"
      />
      <ScrollContainer ref={scrollContainerRef}>
        {dates.map((date) => {
          const display = getDateDisplay(date);
          return (
            <DateButton
              key={date.format('YYYY-MM-DD')}
              $isSelected={date.isSame(selectedDate, 'day')}
              $bgColor={COLORS.grey.primary}
              $hoverColor={COLORS.grey.secondaryLight}
              onClick={() => onDateSelect(date)}
            >
              <ContentContainer>
                {display.topContent && <DayText>{display.topContent}</DayText>}
                {display.middleContent && (
                  <DateText>{display.middleContent}</DateText>
                )}
                {display.bottomContent && (
                  <MonthText>{display.bottomContent}</MonthText>
                )}
              </ContentContainer>
            </DateButton>
          );
        })}
      </ScrollContainer>
      <NavigationButton
        onClick={() => navigateWeek('next')}
        icon={<RightOutlined />}
        size="small"
      />
    </WeekContainer>
  );
};

const WeekContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const NavigationButton = styled(Button)`
  min-width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${COLORS.white.primary};
  border: 1px solid #d9d9d9;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${COLORS.grey.secondary} !important;
    border-color: ${COLORS.grey.secondary} !important;
    color: ${COLORS.white.primary} !important;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  overflow: visible;
  gap: 32px;
  padding: 8px 0;
  flex: 1;
  justify-content: space-between;
`;

const ContentContainer = styled.div<{ $iconPosition?: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  justify-content: center;
`;

const DateButton = styled(Button)<{
  $isSelected?: boolean;
  $bgColor?: string;
  $hoverColor?: string;
}>`
  flex: 1;
  min-width: 62px;
  height: 126px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background-color: ${({ $isSelected, $bgColor }) =>
    $isSelected ? $bgColor : 'transparent'};
  border: 1px solid
    ${({ $isSelected, $bgColor }) => ($isSelected ? $bgColor : '#d9d9d9')};
  color: ${({ $isSelected }) =>
    $isSelected ? COLORS.white.primary : 'inherit'};
  transition: all 0.2s ease;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background-color: ${({ $isSelected, $hoverColor, $bgColor }) =>
      $isSelected ? $bgColor : $hoverColor} !important;
    border-color: ${({ $hoverColor }) => $hoverColor} !important;
    color: ${COLORS.white.primary} !important;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const DayText = styled.span`
  font-weight: ${FONT_WEIGHTS.SEMIBOLD};
  margin-bottom: 4px;
`;

const DateText = styled.span`
  font-size: ${TEXT_SIZES.BASE}px;
  font-weight: ${FONT_WEIGHTS.BOLD};
  margin-bottom: 4px;
`;

const MonthText = styled.span`
  font-size: ${TEXT_SIZES.XS}px;
`;

export default DateScroller;
